import config from '../config';
import selectedEnvironment from '../env';
import JSZip from "jszip";
const API_URL = `${config[selectedEnvironment].API_URL}`;
const References_API_URL = `${API_URL}/References`;
const OCR_API_URL = `${API_URL}/OcrImport`;


const fetchAndExtract = async (url, options) => {
    const res = await fetch(url, options);
    if (!res.ok) throw new Error(await res.text());

    const cType = res.headers.get("Content-Type") || "";
    const blob = await res.blob();

    // a normal PDF → return single‑element array
    if (!cType.includes("zip")) return blob;

    // -----------------------------------------------------------
    // ZIP: extract every *.pdf and **wrap it in a new Blob**
    // -----------------------------------------------------------
    const zip = await JSZip.loadAsync(blob);

    const pdfBlobs = await Promise.all(
        Object.keys(zip.files)
            .filter(name => name.toLowerCase().endsWith(".pdf"))
            .map(async name => {
                const uint8 = await zip.files[name].async("uint8array");
                return new Blob([uint8], { type: "application/pdf" }); // ★ here
            })
    );

    return pdfBlobs;
};


const UploadDocument = async (type, formData, auth) => {
    const response = await fetch(`${API_URL}/${type}/Create`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
        },
        body: formData,
    });
    return response;
};

const UploadDocumentPayload = async (type, payload, auth) => {
    const response = await fetch(`${API_URL}/${type}/Create`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json",  // Make sure to set JSON content type
        },
        body: JSON.stringify(payload),  // Convert the payload to a JSON string
    });
    return response;
};

const UploadDocumentPayloadWithDocument = async (type, payload, auth) => {
    const isFormData = payload instanceof FormData;

    const response = await fetch(`${API_URL}/${type}/Create`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${auth}`,
            ...(!isFormData && { 'Content-Type': 'application/json' }) // header only for JSON
        },
        body: isFormData ? payload : JSON.stringify(payload)
    });

    return response;
};

const UploadDocumentJournalEntryWithDocument = async (type, payload, auth) => {
    const isFormData = payload instanceof FormData;

    const response = await fetch(`${API_URL}/JournalEntries/CreateWithDocument`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${auth}`,
            ...(!isFormData && { 'Content-Type': 'application/json' }) // header only for JSON
        },
        body: isFormData ? payload : JSON.stringify(payload)
    });

    return response;
};

const GetJournalEntriesByCaseId = async (caseId, auth) => {
    const response = await fetch(`${API_URL}/JournalEntries/${caseId}`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${auth}`,
            'Content-Type': 'application/json'
        }
    });

    return response;
};

const CreateImportManifestEntry = async (payload, auth) => {
    const isFormData = payload instanceof FormData;

    const response = await fetch(`${API_URL}/JournalEntries/CreateImportManifestEntry`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${auth}`,
            ...(!isFormData && { 'Content-Type': 'application/json' }) // header only for JSON
        },
        body: isFormData ? payload : JSON.stringify(payload)
    });

    return response;
};

const CreateImportManifestByOCR = async (payload, auth) => {
    const response = await fetch(`${API_URL}/JournalEntries/CreateByOCR`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
        },
        body: payload,
    });

    return response;
};

const CreateCustomsAgentInvoiceByOCR = async (payload, auth) => {
    const response = await fetch(`${API_URL}/JournalEntries/CreateCustomsAgentInvoiceByOCR`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
        },
        body: payload,
    });

    return response;
};

const UploadDocumentPayloads = async (type, payload, auth) => {
    const response = await fetch(`${API_URL}/${type}/CreateMultiple`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json",  // Make sure to set JSON content type
        },
        body: JSON.stringify(payload),  // Convert the payload to a JSON string
    });

    return response;
};

const UploadOCRDocument = async (type, formData, auth) => {
    const response = await fetch(`${API_URL}/${type}/CreateByOCR`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
        },
        body: formData,
    });
    return response
};

const UploadOCRDocumentV2 = async (type, formData, auth) => {
    const response = await fetch(`${OCR_API_URL}/${type}`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
        },
        body: formData,
    });
    return response
};

const ASYNC_OCR_API_URL = `${API_URL}/AsyncOcrImport`;
const ASYNC_PA_OCR_API_URL = `${API_URL}/async-pa-ocr`;

const AsyncUploadOCRDocumentV2 = async (type, formData, auth) => {
    const response = await fetch(`${ASYNC_OCR_API_URL}/${type}`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
        },
        body: formData,
    });
    return response;
};

// Client Expense Assignment report: expense ids that have a NON-canceled tax invoice generated
// from them. Used to mark those expense rows as invoiced (yellow) on the report.
const GetInvoicedExpenseIds = async (caseId, auth) => {
    const response = await fetch(`${API_URL}/Incomes/GetInvoicedExpenseIds/${caseId}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
        },
    });
    if (!response.ok) return [];
    return await response.json();
};

const GetActiveJobsByCaseId = async (caseId, auth) => {
    const response = await fetch(`${ASYNC_OCR_API_URL}/Active/${caseId}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
        },
    });
    if (!response.ok) return [];
    return await response.json();
};

const GetActiveJobsByAccountantId = async (accountantId, auth) => {
    const response = await fetch(`${ASYNC_OCR_API_URL}/ActiveByAccountant/${accountantId}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
        },
    });
    if (!response.ok) return [];
    return await response.json();
};

const GetAsyncOcrJobStatus = async (jobId, auth) => {
    const response = await fetch(`${ASYNC_OCR_API_URL}/Status/${jobId}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
        },
    });
    if (!response.ok) return null;
    return await response.json();
};

const CancelAsyncOcrJob = async (jobId, auth) => {
    const response = await fetch(`${ASYNC_OCR_API_URL}/Cancel/${jobId}`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
        },
    });
    let body = null;
    try { body = await response.json(); } catch { /* server may return empty body on 404 */ }
    return { ok: response.ok, status: response.status, body };
};

const AsyncImportPaymentAcceptances = async (formData, auth) => {
    const response = await fetch(`${ASYNC_PA_OCR_API_URL}/Import`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
        },
        body: formData,
    });
    return response;
};

const GetAsyncPaOcrJobStatus = async (jobId, auth) => {
    const response = await fetch(`${ASYNC_PA_OCR_API_URL}/Status/${jobId}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
        },
    });
    if (!response.ok) return null;
    return await response.json();
};

const CancelAsyncPaOcrJob = async (jobId, auth) => {
    const response = await fetch(`${ASYNC_PA_OCR_API_URL}/Cancel/${jobId}`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
        },
    });
    let body = null;
    try { body = await response.json(); } catch { /* may be empty on 404 */ }
    return { ok: response.ok, status: response.status, body };
};

const UploadOCRPaymentAcceptanceV2 = async (formData, auth) => {
    const response = await fetch(`${API_URL}/PaymentAcceptanceOcr/Process`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
        },
        body: formData,
    });
    return response
};

const ImportPaymentAcceptances = async (formData, auth) => {
    const response = await fetch(`${API_URL}/PaymentAcceptanceOcr/Import`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
        },
        body: formData,
    });
    return response
};

const GetDocumentbyDate = async (type, CaseId, date, auth) => {

    const response = await fetch(`${API_URL}/${type}/byDate`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, date })
    });

    return response;
};

const GetDocumentbyDateRange = async (type, CaseId, date, EndDate, auth, options = {}) => {
    const response = await fetch(`${API_URL}/${type}/byDateRange`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            CaseId,
            date,
            EndDate,
            ...(options.exactDocumentDates ? { exactDocumentDates: true } : {}),
        })
    });

    return response;
};

const GetDocumentbyRegistrationDate = async (type, CaseId, date, auth) => {
    const response = await fetch(`${API_URL}/${type}/byRegistrationDate`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, date })
    });

    return response;
};

const GetDocumentbyRegistrationDateRange = async (type, CaseId, date, EndDate, auth) => {
    const response = await fetch(`${API_URL}/${type}/byRegistrationDateRange`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, date, EndDate })
    });

    return response;
};

const GetAmountChecksType = async (type, CaseId, CheckType, Date, auth) => {
    const response = await fetch(`${API_URL}/${type}/byCheckTypeAmount`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, CheckType, Date })
    });
    const data = await response.json();
    return data;
};

const GetAmountChecksRangeType = async (type, CaseId, CheckType, Date, EndDate, auth) => {
    const response = await fetch(`${API_URL}/${type}/byCheckTypeRangeAmount`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, CheckType, Date, EndDate })
    });
    const data = await response.json();
    return data;
};


const GetWithholdingTaxType = async (type, CaseId, PaymentAcceptanceType, Date, auth) => {
    const response = await fetch(`${API_URL}/${type}/byAcceptanceTypeAmountWithholdingTax`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, PaymentAcceptanceType, Date })
    });
    return response;
};

const GetWithholdingRangeType = async (type, CaseId, PaymentAcceptanceType, Date, EndDate, auth) => {
    const response = await fetch(`${API_URL}/${type}/byAcceptanceTypeRangeWithholdingTax`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, PaymentAcceptanceType, Date, EndDate })
    });
    return response;
};

const GetWithholdingRange = async (type, CaseId, Date, EndDate, auth) => {
    const response = await fetch(`${API_URL}/${type}/byRangeWithholdingTax`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, Date, EndDate })
    });
    return response;
};
const GetAmountDocumentsbyDate = async (type, CaseId, date, auth) => {
    const response = await fetch(`${API_URL}/${type}/byDateAmount`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, date })
    });

    return response;
};

const GetPurchaseOfEquipmentAmountDocumentsbyDate = async (type, CaseId, date, auth) => {
    const response = await fetch(`${API_URL}/${type}/byPurchaseOfEquipmentsortCodesDateAmount`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, date })
    });

    return response;
};

const GetVatAmountDocumentsbyDate = async (type, CaseId, date, auth) => {
    const response = await fetch(`${API_URL}/${type}/byDateVatAmount`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, date })
    });

    return response;
};

const GetPurchaseOfEquipmentVatAmountDocumentsbyDate = async (type, CaseId, date, auth) => {
    const response = await fetch(`${API_URL}/${type}/byPurchaseOfEquipmentDateVatAmount`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, date })
    });

    return response;
};

const GetAmountDocumentsbyDateRange = async (type, CaseId, date, EndDate, auth) => {
    const response = await fetch(`${API_URL}/${type}/byDateRangeAmount`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, date, EndDate })
    });

    return response;
};


const GetVatAndAmountDocumentsbyDateRange = async (type, CaseId, date, EndDate, auth, detailed = false) => {
    const response = await fetch(`${API_URL}/${type}/byDateRangeVatAndAmount`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, date, EndDate, Detailed: detailed })
    });

    return response;
};

const GetAmountWithNoVATDocumentsbyDateRange = async (type, CaseId, date, EndDate, auth) => {
    const response = await fetch(`${API_URL}/${type}/byDateRangeNetAmount`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, date, EndDate })
    });

    return response;
};

const GetZeroVatByDateRangeAmount = async (type, CaseId, date, EndDate, auth) => {
    const response = await fetch(`${API_URL}/${type}/zeroVatbyDateRangeAmount`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, date, EndDate })
    });

    return response;
};
const GetPurchaseOfEquipmentAmountDocumentsbyDateRange = async (type, CaseId, date, EndDate, auth) => {
    const response = await fetch(`${API_URL}/${type}/byPurchaseOfEquipmentDateRangeAmount`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, date, EndDate })
    });

    return response;
};

const GetVatAmountDocumentsbyDateRange = async (type, CaseId, date, EndDate, auth) => {
    const response = await fetch(`${API_URL}/${type}/byDateRangeVATAmount`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, date, EndDate })
    });

    return response;
};

const GetExpenseVatsAmountbyDateRange = async (CaseId, date, EndDate, auth) => {
    const response = await fetch(`${API_URL}/Expenses/byDateRangeVatAmounts`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, date, EndDate })
    });

    return response;
};

const GetPurchaseOfEquipmentVatAmountDocumentsbyDateRange = async (type, CaseId, date, EndDate, auth) => {
    const response = await fetch(`${API_URL}/${type}/byPurchaseOfEquipmentDateRangeVATAmount`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, date, EndDate })
    });

    return response;
};

const DeleteDocument = async (type, id, auth) => {
    const response = await fetch(`${API_URL}/${type}/Delete/${id}`, {
        method: 'DELETE',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
    });

    return response;
};

/**
 * Normalize document payload so the API receives null/number instead of empty strings
 * for nullable decimal and enum fields (avoids "could not be converted to decimal" errors).
 */
const normalizeDocumentForUpdate = (doc) => {
    const normalized = { ...doc };
    // foreignCurrencyAmount: API expects decimal? (number or null), not ""
    if (normalized.foreignCurrencyAmount === '' || normalized.foreignCurrencyAmount === undefined) {
        normalized.foreignCurrencyAmount = null;
    } else if (typeof normalized.foreignCurrencyAmount === 'string') {
        const parsed = parseFloat(normalized.foreignCurrencyAmount);
        normalized.foreignCurrencyAmount = Number.isNaN(parsed) ? null : parsed;
    }
    // foreignCurrencyCode: API expects enum? (number or null), not ""
    if (normalized.foreignCurrencyCode === '' || normalized.foreignCurrencyCode === undefined) {
        normalized.foreignCurrencyCode = null;
    } else if (typeof normalized.foreignCurrencyCode === 'string') {
        const parsed = parseInt(normalized.foreignCurrencyCode, 10);
        normalized.foreignCurrencyCode = Number.isNaN(parsed) ? null : parsed;
    }
    // exchangeRate: API expects decimal? (number or null), not ""
    if (normalized.exchangeRate === '' || normalized.exchangeRate === undefined) {
        normalized.exchangeRate = null;
    } else if (typeof normalized.exchangeRate === 'string') {
        const parsed = parseFloat(normalized.exchangeRate);
        normalized.exchangeRate = Number.isNaN(parsed) ? null : parsed;
    }
    // exchangeRateType: API expects enum? (number or null), not ""
    if (normalized.exchangeRateType === '' || normalized.exchangeRateType === undefined) {
        normalized.exchangeRateType = null;
    } else if (typeof normalized.exchangeRateType === 'string') {
        const parsed = parseInt(normalized.exchangeRateType, 10);
        normalized.exchangeRateType = Number.isNaN(parsed) ? null : parsed;
    }
    return normalized;
};

const UpdateDocument = async (type, auth, Document) => {
    const payload = normalizeDocumentForUpdate(Document);
    const response = await fetch(`${API_URL}/${type}/Update`, {
        method: 'PUT',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
    });

    return response;
};

const DownloadCustomDocuments = async (type, auth, dto) => {
    const response = await fetch(`${API_URL}/${type}/DownloadCustom`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(dto)
    });

    if (response.ok) {
        // Assuming the server sends back a URL to download the zip file
        // Or directly handles the file download on the client side
        return await response.blob(); // This assumes the response is a file to be downloaded
    } else {
        throw new Error('Failed to download documents');
    }
};

const DownloadDocument = async (auth, dto) => {
    const response = await fetch(`${API_URL}/Documents/DownloadDocument`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(dto)
    });

    if (response.ok) {
        return await response.blob();
    } else {
        throw new Error('Failed to download document');
    }
};

const DownloadDocumentsBulk = async (auth, dto) => {
    const response = await fetch(`${API_URL}/Documents/DownloadDocumentsBulk`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(dto)
    });

    if (response.ok) {
        return await response.blob();
    } else {
        throw new Error('Failed to download documents');
    }
};

const CheckDigitalSignature = async (auth, dto) => {
    const response = await fetch(`${API_URL}/Documents/CheckDigitalSignature`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(dto)
    });

    if (response.ok) {
        return await response.json();
    } else {
        throw new Error('Failed to check digital signature capability');
    }
};

const GetOtherDocumentbyType = async (type, CaseId, DocumentType, auth) => {
    const response = await fetch(`${API_URL}/${type}/byType`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, DocumentType })
    });

    return response;
};

const GetOtherDocumentsByTypes = async (CaseId, DocumentTypes, auth) => {
    const response = await fetch(`${API_URL}/OtherDocuments/byTypes`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, OtherDocumentsTypeIds: DocumentTypes })
    });

    return response;
};

const AttachOtherDocumentFiles = async (id, files, auth) => {
    const formData = new FormData();
    formData.append('id', id);
    files.forEach((file) => formData.append('documents', file, file.name));
    return fetch(`${API_URL}/OtherDocuments/AttachFiles`, {
        method: 'POST',
        headers: { "Authorization": 'Bearer ' + auth },
        body: formData
    });
};

const DeleteOtherDocumentFile = async (id, auth) => fetch(`${API_URL}/OtherDocuments/DeleteFile/${id}`, {
    method: 'DELETE',
    headers: { "Authorization": 'Bearer ' + auth }
});

const CreateInvoice = async (type, Document, auth) => {
    const response = await fetch(`${API_URL}/${type}/GenerateIncomePdf`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ ...Document })
    });

    if (response.ok) {
        return response.blob();  // Returns a Blob instead of the usual JSON
    } else {
        throw new Error('Failed to fetch the PDF');
    }
};

const CreateNewInvoice = async (
    controller,
    endpoint,
    payload,
    type,
    title,
    TransactionDocumentType,
    auth
) =>
    fetchAndExtract(`${API_URL}/${controller}/${endpoint}`, {
        method: "POST",
        headers: {
            Authorization: "Bearer " + auth,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(
            title && TransactionDocumentType
                ? { ...payload, type, title, TransactionDocumentType }
                : { ...payload, type }
        ),
    });

const CreateInvoiceCustomized = async (controller, endpoint, Document, type, title, TransactionDocumentType, auth) => {
    try {
        const response = await fetch(`${API_URL}/${controller}/${endpoint}`, {
            method: 'POST',
            headers: {
                "Authorization": 'Bearer ' + auth,
                "Content-Type": "application/json"
            },
            body: title && TransactionDocumentType ? JSON.stringify({ ...Document, type, title, TransactionDocumentType }) : JSON.stringify({ ...Document, type })
        });

        if (!response.ok) {
            throw new Error('Failed to fetch the PDF');
        }

        const blob = await response.blob();

        // Create a URL for the file
        const fileUrl = window.URL.createObjectURL(blob);

        // Create a temporary link and trigger the download
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', `IncomeReport-${Document.CaseId}.pdf`); // Fixed filename
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } catch (error) {
        console.error('Error creating invoice and downloading file:', error);
        alert('Error creating invoice and downloading file. Please try again.');
    }
};

const CreateInvoiceWithDocuments = async (controller, endpoint, formData, caseId, auth) => {

    try {
        const response = await fetch(`${API_URL}/${controller}/${endpoint}`, {
            method: 'POST',
            headers: {
                "Authorization": 'Bearer ' + auth,
                // 'Content-Type': 'multipart/form-data',
                // 'Accept': 'application/json'
            },
            body: formData
        });

        if (!response.ok) {
            throw new Error(await response.text());
            // response.json().then(errorDetails => {
            //     console.error('Failed to fetch the PDF', errorDetails);
            // });
        }


        // Get the file content from the response
        const fileContent = await response.blob();

        // Create a URL for the file
        const fileUrl = window.URL.createObjectURL(fileContent);

        // Create a temporary link and trigger the download
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', 'receipt.pdf'); // Set the correct file name here
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        return fileContent;
    } catch (error) {
        console.error('Error creating invoice and opening file:', error);
        throw new Error(error);
    }
};

const CreateInvoiceWithDocumentsAndId = async (controller, endpoint, formData, caseId, auth) => {
    try {
        const response = await fetch(`${API_URL}/${controller}/${endpoint}`, {
            method: 'POST',
            headers: {
                "Authorization": 'Bearer ' + auth,
                // 'Content-Type': 'multipart/form-data',
                // 'Accept': 'application/json'
            },
            body: formData
        });

        if (!response.ok) {
            throw new Error(await response.text());
        }

        // Return the response object so the caller can handle JSON or blob
        return response;
    } catch (error) {
        console.error('Error creating invoice with documents and ID:', error);
        throw new Error(error);
    }
};

const GetDocumentById = async (type, CaseId, documentId, token) => {

    const response = await fetch(`${API_URL}/${type}/GetById`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + token,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, documentId })
    });
    return response;
};

const GetPaymentAcceptancesByRelatedSource = async (caseId, { incomeId, expenseId, journalEntryId }, token) => {
    const params = new URLSearchParams({ caseId });
    if (incomeId) params.append('incomeId', incomeId);
    if (expenseId) params.append('expenseId', expenseId);
    if (journalEntryId) params.append('journalEntryId', journalEntryId);

    const response = await fetch(`${API_URL}/PaymentAcceptances/ByRelatedSource?${params.toString()}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + token,
            "Content-Type": "application/json"
        }
    });
    return response;
};

const TransferDocument = async (dto, auth) => {
    const response = await fetch(`${API_URL}/Documents/TransferDocument`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(dto),
    });

    return response;
};

const GetInternalByAccountCodeIdDateRange = async (CaseId, Date, EndDate, accountCodeId, auth, isForBankTransactions = false) => {
    const response = await fetch(`${API_URL}/Documents/byDateRangeAccountCodeId`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, Date, EndDate, accountCodeId, isForBankTransactions })
    });

    return response;
};

const GetInternalByAccountCodeIdDateRangeWithCreditAndDebit = async (CaseId, Date, EndDate, accountCodeId, auth, Currency = null, FilterByDocumentDate = false) => {
    const response = await fetch(`${API_URL}/Documents/byDateRangeAccountCodeIdWithCreditAndDebit`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, Date, EndDate, accountCodeId, Currency, FilterByDocumentDate })
    });

    return response;
};

/** The open items of many account cards in one call, for reports that cover hundreds of cards. */
const GetOpenItemsByAccountCodeIds = async (CaseId, Date, EndDate, AccountCodeIds, auth, Currency = null) => {
    const response = await fetch(`${API_URL}/Documents/openItemsByAccountCodeIds`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ CaseId, Date, EndDate, AccountCodeIds, Currency })
    });

    return response;
};

const CreateDraftInvoice = async (
    controller,
    endpoint,
    payload,
    type,
    title,
    TransactionDocumentType,
    auth
) =>
    fetchAndExtract(`${API_URL}/${controller}/${endpoint}`, {
        method: "POST",
        headers: {
            Authorization: "Bearer " + auth,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(
            title && TransactionDocumentType
                ? { ...payload, type, title, TransactionDocumentType }
                : { ...payload, type }
        ),
    });

const CreateDraftReceipt = async (
    controller,
    endpoint,
    payload,
    auth
) =>
    fetchAndExtract(`${API_URL}/${controller}/${endpoint}`, {
        method: "POST",
        headers: {
            Authorization: "Bearer " + auth,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });
const CreateComplexDraftInvoice = async (
    controller,
    endpoint,
    payload,
    type,
    title,
    TransactionDocumentType,
    auth
) =>
    fetchAndExtract(`${API_URL}/${controller}/${endpoint}`, {
        method: "POST",
        headers: {
            Authorization: "Bearer " + auth,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(
            title && TransactionDocumentType
                ? { ...payload, type, title, TransactionDocumentType }
                : { ...payload, type }
        ),
    });
const BalanceControl = async (caseId, startDate, endDate, token, onlyProcessed = false, useRegistrationMonth = true) => {
    const response = await fetch(`${API_URL}/Documents/BalanceControl`, {
        method: 'POST',
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            caseId,
            startDate,
            endDate,
            onlyProcessed,
            useRegistrationMonth
        })
    });
    return response
};

const BalanceControlForJournalEntries = async (caseId, startDate, endDate, token, onlyProcessed = false, useRegistrationMonth = true) => {
    const response = await fetch(`${API_URL}/Documents/BalanceControlForJournalEntries`, {
        method: 'POST',
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            caseId,
            startDate,
            endDate,
            onlyProcessed,
            useRegistrationMonth
        })
    });
    return response;
};

const BalanceControlForExpenses = async (caseId, startDate, endDate, token, onlyProcessed = false, useRegistrationMonth = true) => {
    const response = await fetch(`${API_URL}/Documents/BalanceControlForExpenses`, {
        method: 'POST',
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            caseId,
            startDate,
            endDate,
            onlyProcessed,
            useRegistrationMonth
        })
    });
    return response;
};

const BalanceControlForIncomes = async (caseId, startDate, endDate, token, onlyProcessed = false, useRegistrationMonth = true) => {
    const response = await fetch(`${API_URL}/Documents/BalanceControlForIncomes`, {
        method: 'POST',
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            caseId,
            startDate,
            endDate,
            onlyProcessed,
            useRegistrationMonth
        })
    });
    return response;
};

const BalanceControlForPaymentAcceptances = async (caseId, startDate, endDate, token, onlyProcessed = false, useRegistrationMonth = true) => {
    const response = await fetch(`${API_URL}/Documents/BalanceControlForPaymentAcceptances`, {
        method: 'POST',
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            caseId,
            startDate,
            endDate,
            onlyProcessed,
            useRegistrationMonth
        })
    });
    return response;
};

const VatPalcodePlacementControl = async (caseId, startDate, endDate, token, onlyProcessed = false, useRegistrationMonth = true) => {
    const response = await fetch(`${API_URL}/Documents/VatPalcodePlacementControl`, {
        method: 'POST',
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            caseId,
            startDate,
            endDate,
            onlyProcessed,
            useRegistrationMonth
        })
    });
    return response;
};

const CalculateCreditDebitSum = async (caseId, startDate, endDate, documentType, token, documentIds = null) => {
    const response = await fetch(`${API_URL}/Documents/CalculateCreditDebitSum`, {
        method: 'POST',
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            caseId,
            startDate,
            endDate,
            documentType,
            documentIds: documentIds || null
        })
    });
    return response;
};

const GetDocumentPageData = async (dto, auth) => {
    const response = await fetch(`${API_URL}/Documents/GetDocumentPageData`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(dto)
    });
    return response;
};

const GetJournalEntryPageData = async (dto, auth) => {
    const response = await fetch(`${API_URL}/JournalEntries/JournalEntryPageLoad`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(dto)
    });
    return response;
};

const GetPaymentAcceptancePageData = async (dto, auth) => {
    const response = await fetch(`${API_URL}/Documents/GetPaymentAcceptancePageData`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(dto)
    });
    return response;
};

const GetProcessedDocumentCountsByMonth = async (caseIds, year, month, auth) => {
    try {
        const response = await fetch(`${API_URL}/Documents/GetProcessedDocumentCountsByMonth`, {
            method: 'POST',
            headers: {
                "Authorization": 'Bearer ' + auth,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ CaseIds: caseIds, Year: year, Month: month })
        });
        if (!response.ok) {
            return [];
        }
        return await response.json();
    } catch (error) {
        console.error("Error retrieving processed document counts by month:", error);
        return [];
    }
};

const GetSellEquipmentAmountDocumentsbyDate = async (caseId, accountantId, StartDate, EndDate, token, withVat = true) => {
    const response = await fetch(`${API_URL}/Incomes/GetSellEquipmentIncomes`, {
        method: 'POST',
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            CaseId: caseId,
            accountantId: accountantId,
            StartDate: StartDate,
            EndDate: EndDate,
            WithVat: withVat
        })
    });

    return response;
};

const GetDocumentUrlById = async (incomeId, auth) => {
    const resp = await fetch(`${API_URL}/Incomes/GetDocumentUrlById`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${auth}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(incomeId),
    });
    return resp;
};

const GetPaymentAcceptanceDocumentUrlById = async (paymentAcceptanceId, auth) => {
    const resp = await fetch(`${API_URL}/PaymentAcceptances/GetDocumentUrlById`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${auth}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentAcceptanceId),     // ← plain Guid
    });
    return resp;                      // caller will .json() & get { url }
};

const GetPostponedChecks = async (caseId, untilDate, startDate, token, isClosedManually = false) => {
    const endOfDay = new Date(untilDate);
    const startOfDay = new Date(startDate);
    endOfDay.setHours(23, 59, 1, 1);
    startOfDay.setHours(1, 1, 1, 1);
    const response = await fetch(`${API_URL}/PaymentAcceptances/PostponedChecks`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + token,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            caseId: caseId,
            untilDate: untilDate,
            startDate: startDate,
            IsCloseManually: isClosedManually
        }),
    });

    if (!response.ok) throw new Error('Failed to fetch postponed checks');
    return response.json();
};


const GeneratePostponedChecksDrafts = async (caseId, checkPairs, generateDate, token) => {

    // checkPairs is an array of objects: { paymentAcceptanceId, checkId }
    // We'll structure the POST body as { caseId, items: checkPairs }
    const response = await fetch(`${API_URL}/DocumentGenerators/GeneratePostPonedChecksDrafts`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify({
            caseId,
            items: checkPairs,
            generateDate
        }),
    });
    if (!response.ok) {
        throw new Error('Failed to generate postponed checks drafts');
    }
    return response.json(); // => array of { fileName, pdfBase64 }
};

export const GeneratePostponedChecks = async (caseId, checkPairs, generateDate, token, decisionFlag = null) => {
  const body = {
    CaseId: caseId,
    Items: checkPairs,
    GenerateDate: generateDate,
    Decision: decisionFlag
  };

  const resp = await fetch(`${API_URL}/DocumentGenerators/GeneratePostPonedChecks`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    // IMPORTANT: surface the real server text (could be JSON or plain text)
    throw new Error(await resp.text());
  }

  // Check the content type of the response
  const contentType = resp.headers.get('content-type');
  
  // If it's a text response (like "Decision processed successfully")
  if (contentType && contentType.includes('text/plain')) {
    const textResponse = await resp.text();
    return { type: 'text', message: textResponse };
  }
  
  // If it's a blob response (PDF/ZIP), handle it as before
  const blob = await resp.blob();
  return { type: 'blob', data: blob };
};


const UpdateAccountCardPhone = async (cardId, caseId, phoneNumber, token) => {
    const response = await fetch(`${API_URL}/AccountCards/UpdatePhoneNumber`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ cardId, caseId, phoneNumber })
    });
    if (!response.ok) {
        throw new Error('Failed to update phone number');
    }
    return response;
};

const ManuallyCloseOrOpen = async (caseId, checkIds, close = true, token) => {

    const response = await fetch(`${API_URL}/PaymentAcceptances/CloseOrOpenPosatPoned`, {
        method: 'PUT',
        headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ caseId, checkIds, close })
    });
    if (!response.ok) throw new Error('Failed to close postponed checks');
    return response.text();
};

/* FAST “exists” call – returns { hasPostponed : true|false } */
const CheckPostponedChecks = async (caseId, untilDate, token, IsCloseManually = false) => {
    // const endOfDay = new Date(untilDate);
    // endOfDay.setHours(23, 59, 59, 999);
    const response = await fetch(
        `${API_URL}/PaymentAcceptances/CheckPostPonedChecks`,
        {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                caseId,
                untilDate: untilDate,         // ISO string is fine
                IsCloseManually,    // backend expects this camel‑cased
            }),
        }
    );


    if (!response.ok) throw new Error('Failed to check postponed checks');
    return response.json();
};



export const GetHoldingItems = async (
    caseId,
    startDate,       // ISO string
    endDate,         // ISO string
    holdingItemType, // "Check" | "Cash" | "CreditCard" | "BankTransfer"
    token,
    isDeposited = false
) => {
    const response = await fetch(
        `${API_URL}/PaymentAcceptances/HoldingItems`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token,
            },
            body: JSON.stringify({
                caseId,
                startDate,
                endDate,
                holdingItemType,
                isDeposited,
            }),
        },
    );

    if (!response.ok) throw new Error('Failed to load holding items');
    return response.json();          // => DocumentDTOs.HoldingItemsResponse
};

export const GenerateDepositDocumentDraft = async (
    caseId,
    accountCardId,      // int
    generateDate,       // ISO string
    type,               // "Check" | "Cash" | "CreditCard" | "BankTransfer"
    items,              // [{ paymentAcceptanceId, transactionId, note?, amount? }, …]
    token,
    description = null, // optional deposit-level description
) => {
    const response = await fetch(
        `${API_URL}/DocumentGenerators/GenerateDepositDocumentDraft`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token,
            },
            body: JSON.stringify({
                caseId,
                accountCardId,
                generateDate,
                type,
                items,
                description,
            }),
        },
    );

    if (!response.ok) throw new Error('Failed to generate deposit draft');
    return response.json();          // { fileName, pdfBase64 }
};

/**
 * Per-check draft preview: server generates ONE draft PDF per item, then merges
 * them into a single PDF (each check on its own page-set) so the existing
 * preview iframe can render the combined preview.
 *
 * Only valid when `type` === Check (the server enforces this).
 * Returns the same shape as {@link GenerateDepositDocumentDraft}: `{ fileName, pdfBase64 }`.
 */
export const GenerateDepositDocumentsPerCheckDraft = async (
    caseId,
    accountCardId,
    generateDate,
    type,               // must be 1 (Check)
    items,
    token,
    description = null,
) => {
    const response = await fetch(
        `${API_URL}/DocumentGenerators/GenerateDepositDocumentsPerCheckDraft`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token,
            },
            body: JSON.stringify({
                caseId,
                accountCardId,
                generateDate,
                type,
                items,
                description,
            }),
        },
    );

    if (!response.ok) {
        const txt = await response.text().catch(() => '');
        throw new Error(`Failed to generate per-check deposit drafts${txt ? `: ${txt}` : ''}`);
    }
    return response.json();          // { fileName, pdfBase64 }
};

async function GenerateDepositDocument(
    caseId,
    accountCardId,
    generateDate,
    type,              // number 0|1|2|3 you already pass
    items,             // [{ paymentAcceptanceId, transactionId, note?, amount? }]
    token,
    description = null, // optional deposit-level description
) {
    const res = await fetch(`${API_URL}/DocumentGenerators/GenerateDepositDocumentPdf`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            caseId,
            accountCardId,
            generateDate,
            type,
            items,
            description,
        }),
    });

    if (!res.ok) {
        // let callers get a useful message
        const txt = await res.text().catch(() => '');
        throw new Error(`Failed to generate deposit PDF${txt ? `: ${txt}` : ''}`);
    }

    // 🔑  <‑‑ **THIS** was `res.json()` before
    return await res.blob();          // ← returns a Blob with application/pdf
}

/**
 * Per-check deposit generation: creates ONE deposit + ONE journal entry for EACH item.
 *
 * Only valid when `type` === Check (the server enforces this).
 *
 * The response is:
 *   - a single PDF blob   when exactly one deposit was produced
 *   - a single ZIP blob   when more than one deposit was produced (containing N PDFs)
 *
 * The response carries two custom headers:
 *   - X-Deposit-Count     number of deposits that succeeded
 *   - X-Deposit-Requested number of deposits that were requested
 *   - X-Deposit-Errors    pipe-separated per-item errors (only present on partial failure)
 *
 * @returns {{ blob: Blob, mimeType: string, isZip: boolean, count: number, requested: number, errors: string | null, fileName: string }}
 */
async function GenerateDepositDocumentsPerCheck(
    caseId,
    accountCardId,
    generateDate,
    type,              // number 0|1|2|3 – must be 1 (Check)
    items,             // [{ paymentAcceptanceId, transactionId, note?, amount? }]
    token,
    description = null,
) {
    const res = await fetch(`${API_URL}/DocumentGenerators/GenerateDepositDocumentsPerCheckPdf`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            caseId,
            accountCardId,
            generateDate,
            type,
            items,
            description,
        }),
    });

    if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`Failed to generate per-check deposits${txt ? `: ${txt}` : ''}`);
    }

    const mimeType   = res.headers.get('Content-Type') || '';
    const isZip      = mimeType.toLowerCase().includes('zip');
    const count      = parseInt(res.headers.get('X-Deposit-Count') || '0', 10) || 0;
    const requested  = parseInt(res.headers.get('X-Deposit-Requested') || '0', 10) || 0;
    const errors     = res.headers.get('X-Deposit-Errors') || null;

    // Try to recover the filename from Content-Disposition; fall back to a sensible default.
    const cd = res.headers.get('Content-Disposition') || '';
    let fileName = isZip
        ? `Deposits-${caseId}.zip`
        : `Deposit-${caseId}.pdf`;
    const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(cd);
    if (match && match[1]) {
        try { fileName = decodeURIComponent(match[1]); } catch (_) { fileName = match[1]; }
    }

    const blob = await res.blob();
    return { blob, mimeType, isZip, count, requested, errors, fileName };
}

/* ─── Deposited journal‑entries ─────────────────────────────── */
const GetDepositedJournalEntriesByDate = async (
    CaseId,
    date,            // ISO string – single date is enough
    EndDate = null,  // optional ISO – send only if you have a range
    auth
) => {
    const body = EndDate
        ? { CaseId, Date: date, EndDate }   // DTO accepts both
        : { CaseId, Date: date };

    const response = await fetch(`${API_URL}/JournalEntries/byDateDeposited`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${auth}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    });

    return response;               // leave it to the caller to .json() it
};

/* ─── Accountant-wide: JEs with VAT but no VAT sort code ───── */
// filters: { accountantId, startDate?, endDate?, caseIds?, includeInactiveClients? }
const ScanVatWithoutVatSortCode = async (filters, auth) => {
    const res = await fetch(`${API_URL}/JournalEntries/vatWithoutVatSortCode/scan`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${auth}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(filters),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
};

// Same filters + optional journalEntryIds / reasons; without ids every scanned entry gets VAT 0.
const ZeroVatWithoutVatSortCode = async (filters, auth) => {
    const res = await fetch(`${API_URL}/JournalEntries/vatWithoutVatSortCode/zeroVat`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${auth}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(filters),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
};

/* ─── Legacy deposit healing ───────────────────────────────── */
const GetLegacyDepositHealingList = async (caseId, date, endDate = null, auth) => {
    const body = endDate ? { caseId, date, endDate } : { caseId, date };
    const res = await fetch(`${API_URL}/JournalEntries/legacyDeposits/healingList`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${auth}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
};

const HealLegacyDeposit = async (caseId, journalEntryId, auth) => {
    const res = await fetch(`${API_URL}/JournalEntries/legacyDeposits/heal`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${auth}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ caseId, journalEntryId }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
};

const HealLegacyDepositManual = async (caseId, journalEntryId, allocations, auth) => {
    const res = await fetch(`${API_URL}/JournalEntries/legacyDeposits/healManual`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${auth}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ caseId, journalEntryId, allocations }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
};


const GetDocumentItems = async (caseId, documentId, token) => {
    const response = await fetch(`${API_URL}/DocumentGenerators/GetDocumentItems`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + token,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ caseId, documentId })
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get document items: ${errorText}`);
    }
    return response.json(); // returns the array of items
};

const GetDocumentIdsByMakats = async (caseId, makats, itemIds, token) => {
    const response = await fetch(`${API_URL}/DocumentGenerators/GetDocumentIdsByMakats`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + token,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ caseId, makats: makats || [], itemIds: itemIds || [] })
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get document IDs by makat: ${errorText}`);
    }
    return response.json();
};

const GetDocumentsItemsSummary = async (caseId, documentIds, token) => {
    const response = await fetch(`${API_URL}/DocumentGenerators/GetDocumentsItemsSummary`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + token,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ caseId, documentIds: documentIds || [] })
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get documents items summary: ${errorText}`);
    }
    return response.json();
};

const AddDocumentItems = async (caseId, documentId, items, token) => {
    const payload = {
        caseId,
        documentId,
        // optionally: documentType: "Income" | "PaymentAcceptance" | "TransactionDocument"
        items: items.map(it => ({
            makat: it.makat || "",
            name: it.name,
            quantity: it.quantity || 0,
            price: it.price || 0,
            discount: it.discount || 0,
            includeVat: it.includeVat !== false, // default true
            itemId: it.itemId || null
        }))
    };

    const response = await fetch(`${API_URL}/DocumentGenerators/AddDocumentItems`, {
        method: 'POST',
        headers: {
            'Authorization': 'Bearer ' + token,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error adding document items: ${errorText}`);
    }
    return response;
};

// `status` is either a boolean (legacy: true = closed) or a DocumentLifecycleStatus value
// (0 = open, 1 = partially processed, 2 = closed).
const SetDocumentStatus = async (documentId, caseId, status, auth) => {
    const processingStatus = typeof status === 'number' ? status : (status ? 2 : 0);
    const response = await fetch(`${API_URL}/Documents/status`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            documentId,
            caseId,
            status: processingStatus === 2,
            processingStatus
        })
    });

    return response;
};

const ResetDocumentStatus = async (documentId, caseId, auth) => {
    const response = await fetch(`${API_URL}/Documents/status/reset`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            documentId,
            caseId
        })
    });

    return response;
};

/**
 * OCR field boxes of an Expense / Income / PaymentAcceptance (see useFieldPositions).
 * 204 = nothing to show.
 */
const GetFieldPositions = async (caseId, entityType, documentId, auth, signal) => {
    const response = await fetch(
        `${API_URL}/Documents/field-positions/${encodeURIComponent(caseId)}/${encodeURIComponent(entityType)}/${documentId}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        signal
    });

    return response;
};

/** "שיוך מסמכים": the chain of a document and the existing documents it can be linked to. */
const GetDocumentLinkOptions = async (caseId, documentId, auth, pairDocumentId = null) => {
    const query = pairDocumentId ? `?pairDocumentId=${encodeURIComponent(pairDocumentId)}` : '';
    const response = await fetch(`${API_URL}/Documents/lifecycle/${encodeURIComponent(caseId)}/${documentId}/link-options${query}`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        }
    });

    return response;
};

/**
 * "שיוך מסמכים" while creating a new document (no id yet): the existing documents the new one — of
 * the given type / customer / amount — can be linked to. `documentType` is a TransactionDocumentType
 * number, `entityType` a DocumentEntityType number, `currency` a ForeignCurrencyCode number.
 */
const GetDraftDocumentLinkOptions = async (
    caseId, { entityType, documentType, customerAccountCodeId, amount, currency, items }, auth) => {
    const response = await fetch(
        `${API_URL}/Documents/lifecycle/${encodeURIComponent(caseId)}/draft-link-options`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            entityType,
            documentType,
            customerAccountCodeId: customerAccountCodeId || null,
            amount: amount ?? 0,
            // ForeignCurrencyCode.ILS = 14. 0 is USD; omitting ILS as 0 hides every shekel source.
            currency: (currency === 0 || currency) ? currency : 14,
            items: Array.isArray(items) ? items : [],
        }),
    });

    return response;
};

/** "שיוך מסמכים": removes one link of a document chain. */
const RemoveDocumentLink = async (caseId, relationshipId, auth) => {
    const response = await fetch(`${API_URL}/Documents/lifecycle/${encodeURIComponent(caseId)}/link/${relationshipId}`, {
        method: 'DELETE',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        }
    });

    return response;
};

/**
 * Lines of a source document (Quotation / Order / …) with what is already invoiced and still to
 * invoice, plus its remaining amount. Resolves to null when the server has nothing for it.
 */
const GetDocumentRemainingItems = async (caseId, documentId, auth) => {
    const response = await fetch(`${API_URL}/Documents/lifecycle/${encodeURIComponent(caseId)}/${documentId}/remaining-items`, {
        method: 'GET',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        }
    });
    if (!response.ok) return null;
    return response.json();
};

/** "שיוך מסמכים": several links at once ({ caseId, links: [{ sourceDocumentId, targetDocumentId, appliedAmount }] }), all or nothing. */
const LinkManyDocuments = async (request, auth) => {
    const response = await fetch(`${API_URL}/Documents/lifecycle/link-many`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(request)
    });

    return response;
};

/** "שיוך מסמכים": links two existing documents. appliedAmount is in the source document's basis. */
const LinkDocuments = async ({ caseId, sourceDocumentId, targetDocumentId, appliedAmount }, auth) => {
    const response = await fetch(`${API_URL}/Documents/lifecycle/link`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            caseId,
            sourceDocumentId,
            targetDocumentId,
            appliedAmount
        })
    });

    return response;
};

const CancelDocument = async (documentId, caseId, auth, alsoCancelSourceDocuments = false) => {
    const response = await fetch(`${API_URL}/Documents/cancel`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            documentId,
            caseId,
            alsoCancelSourceDocuments: !!alsoCancelSourceDocuments
        })
    });

    return response;
};

const LockDocument = async (documentId, caseId, auth) => {
    const response = await fetch(`${API_URL}/Documents/lock`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            documentId,
            caseId
        })
    });

    return response;
};

const BulkEmailDocuments = async (payload, token) => {
    const response = await fetch(`${API_URL}/Documents/BulkEmailDocuments`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + token,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to send email: ${errorText}`);
    }

    return response.json(); // or return true; if no content is expected
};

const BulkWhatsAppDocuments = async (payload, token) => {
    const response = await fetch(`${API_URL}/Documents/BulkWhatsAppDocuments`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + token,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to send WhatsApp messages: ${errorText}`);
    }

    return response.json();
};

const CreateStornoEntries = async (documentIds, caseId, token) => {
    const response = await fetch(`${API_URL}/Documents/storno`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + token,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            DocumentIds: documentIds,
            CaseId: caseId
        })
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create storno entries: ${errorText}`);
    }

    return response.json(); // Will return the created entries info
};


const TransactionSearch = async (payload, token) => {

    const response = await fetch(`${API_URL}/Documents/SearchTransactions`, {
        method: 'POST',
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Transaction search failed: ${errorText}`);
    }

    return response.json(); // will return TransactionSearchResultDto
};



const LockDocumentsByTypeAndIds = async (
    caseId,
    accountantId,
    documentIds,    // array<Guid>
    token
) => {
    const payload = {
        DocumentIds: documentIds,
        CaseId: caseId,
        AccountantId: accountantId,
    };

    return fetch(`${API_URL}/Documents/lock/bulk`, {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });
};

const AddReference = async (dto, auth) =>
    fetch(`${References_API_URL}/CreateReference`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth}`,
        },
        body: JSON.stringify(dto),
    }).then(r => r.json());

const EditReference = async (dto, auth) =>
    fetch(`${References_API_URL}/UpdateReference`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth}`,
        },
        body: JSON.stringify(dto),
    }).then(r => r.json());

const UpdateSpecificReference = async (dto, auth) =>
    fetch(`${References_API_URL}/UpdateReferenceSpecific`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth}`,
        },
        body: JSON.stringify(dto),
    }).then(r => r.json());

const GetReferenceByCaseId = async (caseId, auth) =>
    fetch(`${References_API_URL}/GetReferenceByCaseId/${caseId}`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${auth}`,
        },
    }).then(r => r.json());



const MergeDocuments = async (payload, authToken) => {
    const res = await fetch(`${API_URL}/Documents/merge-documents`, {
        method: 'POST',
        headers: {
            'Authorization': 'Bearer ' + authToken,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
    });

    if (!res.ok) {
        // surface server-side message if present
        const text = await res.text();
        throw new Error(text || 'Merge failed');
    }

    return res.json();          // { documentUrl, … }
};

const uploadOrMergeDocument = async (formData, authToken) => {
    const res = await fetch(`${API_URL}/Documents/upload-or-merge-document`, {
        method: 'POST',
        headers: {
            'Authorization': 'Bearer ' + authToken,
            // Don't set Content-Type - let browser set it with boundary for multipart/form-data
        },
        body: formData
    });

    return res; // Return the response object so caller can check res.ok
};

const deleteDocumentUrl = async (documentId, caseId, documentType, authToken) => {
    const res = await fetch(`${API_URL}/Documents/delete-document-url`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + authToken,
        },
        body: JSON.stringify({
            documentId,
            caseId,
            documentType
        })
    });

    return res; // Return the response object so caller can check res.ok
};

const BulkDeleteDocuments = async (docType, ids, caseId, token) => {
    const url = `${API_URL}/Documents/BulkDelete`;
    const body = { docType, ids, caseId };

    return fetch(url, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
    });
};

/**
 * Finds duplicate groups across all dates/types for a case (expense↔journal etc.).
 * Optional docType focuses results to groups that include that type.
 * Returns the response body (`{ groups: [...] }`); the modal normalizes casing.
 */
const FindDuplicates = async (
    caseId,
    docType,
    startDate,
    endDate,
    token,
    { isRange = false, useRegistrationMonth = false, referenceLastDigits = null } = {}
) => {
    const url = `${API_URL}/Documents/FindDuplicates`;
    const body = {
        caseId,
        docType,
        // Dates kept for API compat; server scans all dates regardless.
        startDate: startDate instanceof Date ? startDate.toISOString() : startDate,
        endDate: endDate instanceof Date ? endDate.toISOString() : endDate,
        isRange,
        useRegistrationMonth,
    };
    if (referenceLastDigits != null) body.referenceLastDigits = referenceLastDigits;

    const res = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || 'Failed to find duplicates');
    }

    return res.json();
};

const BulkUpdateDocuments = async (docType, ids, patch, caseId, token, options = {}) => {
    const url = `${API_URL}/Documents/BulkUpdate`;
    const { flipAmount, flipCards, replaceDescriptionWithAccountName, syncProject, projectId, paymentLineCards, alignRegistrationMonthToDocumentDate } = options;
    const body = { docType, ids, patch, caseId };
    if (flipAmount === true) body.flipAmount = true;
    if (flipCards === true) body.flipCards = true;
    if (alignRegistrationMonthToDocumentDate === true) body.alignRegistrationMonthToDocumentDate = true;
    if (paymentLineCards && Object.keys(paymentLineCards).length > 0) body.paymentLineCards = paymentLineCards;
    if (replaceDescriptionWithAccountName === true) body.replaceDescriptionWithAccountName = true;
    if (syncProject === true) {
        body.syncProject = true;
        body.projectId = projectId ?? null;
    }

    return fetch(url, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
    });
};

/**
 * Replaces oldCardId with newCardId on one side ('debit' | 'credit') of one payment-line type
 * across the case's unlocked payments of paymentAcceptanceType. dryRun = count only.
 * Resolves to { documents, lines }.
 */
const ReplacePaymentLineCard = async (
    { caseId, lineType, paymentAcceptanceType, side, oldCardId, newCardId, dryRun = false },
    token
) => {
    return fetch(`${API_URL}/PaymentAcceptances/ReplaceLineCard`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ caseId, lineType, paymentAcceptanceType, side, oldCardId, newCardId, dryRun })
    });
};

const DuplicateDocument = async (docType, id, patch, caseId, token, options = {}) => {
    const url = `${API_URL}/Documents/Duplicate`;
    const body = {
        docType,
        id,
        patch,
        caseId,
        skipDuplicateMarker: !!options.skipDuplicateMarker,
        skipJournalGroup: !!options.skipJournalGroup
    };
    return fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
    });
};

const SplitDocument = async (docType, sourceId, lines, caseId, token) => {
    return fetch(`${API_URL}/Documents/Split`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ sourceId, docType, caseId, lines })
    });
};

const BatchDuplicateDocuments = async (docType, sourceId, patches, caseId, token, options = {}) => {
    // For batch duplicates, we duplicate the source multiple times with different patches
    // This will create multiple documents and link them in a journal group
    const results = [];
    const errors = [];
    
    for (let i = 0; i < patches.length; i++) {
        try {
            const response = await DuplicateDocument(docType, sourceId, patches[i], caseId, token, options);
            if (response.ok) {
                const result = await response.json();
                results.push(result);
            } else {
                const errorText = await response.text();
                errors.push({ index: i, error: errorText });
            }
        } catch (error) {
            errors.push({ index: i, error: error.message });
        }
    }
    
    return {
        ok: errors.length === 0,
        results,
        errors,
        successCount: results.length,
        totalCount: patches.length
    };
};

const CheckReferenceContinuity = async (caseId, startDate, endDate, token) => {
    const response = await fetch(`${API_URL}/Incomes/CheckReferenceContinuity`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token,
        },
        body: JSON.stringify({
            caseId: caseId,
            startDate: startDate,
            endDate: endDate
        })
    });
    
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
};

const PrintChecks = async (requestData, token) => {
    const response = await fetch(`${API_URL}/PaymentAcceptances/PrintChecks`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error printing check');
    }

    // Return the HTML content directly since backend now returns HTML
    return await response.text();
};

const PrintChecksBatch = async (requestData, token) => {
    const response = await fetch(`${API_URL}/PaymentAcceptances/PrintChecksBatch`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error printing checks');
    }

    return await response.text();
};

const GetLastCheckNumber = async (caseId, token) => {
    const response = await fetch(`${API_URL}/PaymentAcceptances/GetLastCheckNumber?caseId=${caseId}`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error fetching last check number');
    }

    return response;
};

const checkConfirmationNumber = async (request, auth) => {
    
    const response = await fetch(`${API_URL}/Documents/confirmationNumber`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + auth,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
    });
    
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
};

const DocumentService = {
    UploadDocument,
    GetDocumentbyDate,
    GetDocumentbyRegistrationDate,
    GetAmountDocumentsbyDate,
    UpdateDocument,
    DeleteDocument,
    GetAmountChecksType,
    GetAmountDocumentsbyDateRange,
    GetAmountChecksRangeType,
    GetDocumentbyDateRange,
    GetDocumentbyRegistrationDateRange,
    DownloadCustomDocuments,
    GetVatAmountDocumentsbyDateRange,
    GetVatAmountDocumentsbyDate,
    GetOtherDocumentbyType,
    GetOtherDocumentsByTypes,
    AttachOtherDocumentFiles,
    DeleteOtherDocumentFile,
    CreateInvoice,
    UploadOCRDocument,
    GetPurchaseOfEquipmentVatAmountDocumentsbyDateRange,
    GetPurchaseOfEquipmentVatAmountDocumentsbyDate,
    GetPurchaseOfEquipmentAmountDocumentsbyDate,
    GetPurchaseOfEquipmentAmountDocumentsbyDateRange,
    CreateNewInvoice,
    CreateInvoiceWithDocuments,
    CreateComplexDraftInvoice,
    CreateInvoiceCustomized,
    GetWithholdingTaxType,
    GetWithholdingRangeType,
    GetDocumentById,
    GetPaymentAcceptancesByRelatedSource,
    TransferDocument,
    UploadDocumentPayload,
    GetInternalByAccountCodeIdDateRange,
    UploadDocumentPayloads,
    GetInternalByAccountCodeIdDateRangeWithCreditAndDebit,
    GetOpenItemsByAccountCodeIds,
    CreateDraftInvoice,
    CreateDraftReceipt,
    BalanceControl,
    BalanceControlForJournalEntries,
    BalanceControlForExpenses,
    BalanceControlForIncomes,
    BalanceControlForPaymentAcceptances,
    VatPalcodePlacementControl,
    CalculateCreditDebitSum,
    GetDocumentPageData,
    GetJournalEntryPageData,
    GetPaymentAcceptancePageData,
    GetProcessedDocumentCountsByMonth,
    GetZeroVatByDateRangeAmount,
    GetSellEquipmentAmountDocumentsbyDate,
    GetAmountWithNoVATDocumentsbyDateRange,
    GetPostponedChecks,
    GeneratePostponedChecksDrafts,
    GetDocumentItems,
    GetDocumentIdsByMakats,
    GetDocumentsItemsSummary,
    AddDocumentItems,
    SetDocumentStatus,
    ResetDocumentStatus,
    GetFieldPositions,
    GetDocumentLinkOptions,
    GetDraftDocumentLinkOptions,
    GetDocumentRemainingItems,
    LinkDocuments,
    LinkManyDocuments,
    RemoveDocumentLink,
    CancelDocument,
    BulkEmailDocuments,
    BulkWhatsAppDocuments,
    GetExpenseVatsAmountbyDateRange,
    GetVatAndAmountDocumentsbyDateRange,
    GetWithholdingRange,
    LockDocument,
    CreateStornoEntries,
    UpdateAccountCardPhone,
    ManuallyCloseOrOpen,
    GeneratePostponedChecks,
    CheckPostponedChecks,
    TransactionSearch,
    LockDocumentsByTypeAndIds,
    GetHoldingItems,
    GenerateDepositDocumentDraft,
    UploadDocumentPayloadWithDocument,
    UploadDocumentJournalEntryWithDocument,
    CreateImportManifestEntry,
    GetJournalEntriesByCaseId,
    CreateImportManifestByOCR,
    CreateCustomsAgentInvoiceByOCR,
    GenerateDepositDocument,
    GenerateDepositDocumentsPerCheck,
    GenerateDepositDocumentsPerCheckDraft,
    GetDepositedJournalEntriesByDate,
    GetLegacyDepositHealingList,
    HealLegacyDeposit,
    ScanVatWithoutVatSortCode,
    ZeroVatWithoutVatSortCode,
    HealLegacyDepositManual,
    GetDocumentUrlById,
    GetPaymentAcceptanceDocumentUrlById,
    AddReference,
    EditReference,
    UpdateSpecificReference,
    GetReferenceByCaseId,
    UploadOCRDocumentV2,
    AsyncUploadOCRDocumentV2,
    GetActiveJobsByCaseId,
    GetActiveJobsByAccountantId,
    GetAsyncOcrJobStatus,
    CancelAsyncOcrJob,
    AsyncImportPaymentAcceptances,
    GetAsyncPaOcrJobStatus,
    CancelAsyncPaOcrJob,
    UploadOCRPaymentAcceptanceV2,
    ImportPaymentAcceptances,
    MergeDocuments,
    uploadOrMergeDocument,
    deleteDocumentUrl,
    BulkDeleteDocuments,
    FindDuplicates,
    BulkUpdateDocuments,
    ReplacePaymentLineCard,
    DuplicateDocument,
    SplitDocument,
    BatchDuplicateDocuments,
    CreateInvoiceWithDocumentsAndId,
    DownloadDocument,
    DownloadDocumentsBulk,
    CheckDigitalSignature,
    CheckReferenceContinuity,
    PrintChecks,
    PrintChecksBatch,
    GetLastCheckNumber,
    checkConfirmationNumber,
    GetInvoicedExpenseIds
};

export default DocumentService;
